import { getPreferredLanguage } from '@/locale/util'
import type { RangeNode, TypeChart } from '@/modules/activities'
import { usePushHistory } from '@/utils/hooks'
import { overloadColor } from '@/utils/style'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'

const MOBILE_NODE_SIZE = 38

interface WrapperProps {
  width: number
  left: number
  background: string
  isIssueNode: boolean
  height?: string
}

const Wrapper = styled.div.attrs<WrapperProps>(props => ({
  style: {
    left: props.left,
    width: props.width,
    height: props.height,
  },
}))<WrapperProps>`
  position: absolute;
  z-index: ${props =>
    overloadColor(props.background) === 'transparent' ? -1 : 1};
  bottom: 0;
  height: ${props => props.height || '25%'};

  background: ${props => overloadColor(props.background)};

  ${props =>
    props.isIssueNode &&
    css`
      top: 50%;
      transform: translateY(-50%);
      background: transparent !important;
      border-left: none !important;
    `};

  ${props =>
    props.background === 'weekend' &&
    css`
      background-image: linear-gradient(
        45deg,
        rgba(128, 128, 128, 0.2) 25%,
        transparent 25%,
        transparent 50%,
        rgba(128, 128, 128, 0.2) 50%,
        rgba(128, 128, 128, 0.2) 75%,
        transparent 75%,
        transparent 100%
      );
      background-size: 32px 32px;
    `};

  ${props => props.theme.media.mobile} {
    height: ${MOBILE_NODE_SIZE}px;
  }
`

const IssueNode = styled.div`
  position: absolute;
  height: calc(100% + 2rem);
  top: calc(2px - 1rem);
  left: 2px;
  padding-top: 2px;

  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    & > aside {
      display: block;
    }

    i {
      text-shadow: 0 0 3px currentColor;
    }
  }
`

interface IssueNodeTooltipProps {
  visible: boolean
  x: number
  y: number
}

const IssueNodeTooltip = styled.aside<IssueNodeTooltipProps>`
  display: ${props => (props.visible ? 'block' : 'none')};
  position: fixed;
  z-index: 10;
  top: ${props => props.y}px;
  left: ${props => props.x}px;

  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  background: ${props => overloadColor(props.theme.colors.gray1)};
  font-weight: 400;
  color: white;
  pointer-events: none;

  > div {
    padding: 4px 0;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid #ffffff1a;
  }

  > div > span {
    display: flex;
    flex-direction: row;
    gap: 0.2rem;
  }
`

const IssueNodeCircle = styled.div<{
  background: string
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: ${props => overloadColor(props.background)};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
`

interface ScheduleRangeProps {
  nodes: RangeNode[]
  /** First hour in calendar */
  startTime: number
  /** Last hour in calendar */
  endTime: number
  /** Width of entire calendar */
  calendarWidth: number
  /** Offset from daylight savings occuring on the day */
  daylightOffset: number
  /** Chart variant - needed for proper positioning calculation */
  variant: TypeChart
  /** Current resolution for monthly/yearly modes */
  resolution?: number

  disableTooltip?: boolean
  disableEvents?: boolean
  isOverview?: boolean
}

export const ScheduleRange: React.FC<ScheduleRangeProps> = ({
  nodes,
  startTime,
  endTime,
  calendarWidth,
  daylightOffset,
  variant = 'day',
  resolution = 24,
  disableTooltip = false,
  disableEvents = false,
  isOverview = false,
}) => {
  const language = getPreferredLanguage()
  const translations = useTranslate({
    nHours: ['common.n-hours', { n: 0 }],
    nMinutes: ['common.n-minutes', { n: 0 }],
  })
  const pushHistory = usePushHistory()

  const [hovered, setHovered] = useState<{
    id: number
    type: 'wrapper' | 'icon'
  } | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null
  )

  if (!nodes.length) return null

  const sortedNodes = [...nodes].sort((a, b) => a.time - b.time)
  const [firstNode, secondNode] = sortedNodes
  const firstTime = firstNode?.time ?? 0
  const lastTime = sortedNodes[sortedNodes.length - 1]?.time ?? 0
  const numHours = lastTime - firstTime
  const background = firstNode?.background ?? 'transparent'
  const isIssueNode = firstNode?.type === 'issue'
  const isInfringementNode = firstNode?.type === 'infringement'
  const isSpecificConditionNode = firstNode?.type === 'specificCondition'

  let hourWidth: number
  let relativePosition: number
  let nodeWidth: number

  if (variant === 'month') {
    const visibleColumns = resolution || endTime - startTime - daylightOffset
    hourWidth = calendarWidth / visibleColumns

    relativePosition = firstTime - startTime

    const duration = Math.max(numHours, 1)
    nodeWidth =
      background === 'weekend' ? hourWidth * duration : hourWidth * duration - 6
  } else {
    const totalDisplayedHours = endTime - startTime - daylightOffset
    hourWidth = calendarWidth / totalDisplayedHours
    relativePosition = firstTime - startTime
    nodeWidth = hourWidth * numHours
  }
  const left = relativePosition * hourWidth

  const width =
    variant === 'month' ? Math.max(nodeWidth, hourWidth * 0.8) : nodeWidth

  function handleClick(evt: React.MouseEvent<HTMLDivElement>) {
    if (!firstNode?.href) return
    pushHistory(firstNode.href, evt)
  }

  function formatTripDuration(start: number, end: number) {
    const diff = end - start

    const hours = Math.floor(diff)
    const minutes = Math.round((diff - hours) * 60)

    if (hours === 0 && minutes === 0) return
    if (hours === 0) return { minutes }
    if (minutes === 0) return { hours }

    return { hours, minutes }
  }

  const duration = formatTripDuration(
    firstNode?.time ?? 0,
    secondNode?.time ?? 0
  )

  return (
    <>
      <Wrapper
        width={width}
        left={left}
        height={
          isOverview ? firstNode?.height : `calc(${firstNode?.height} - 10px)`
        }
        background={background}
        isIssueNode={
          isIssueNode || isSpecificConditionNode || isInfringementNode
        }
        onMouseEnter={() => setHovered({ id: firstTime, type: 'wrapper' })}
        onMouseLeave={() => setHovered(null)}
        onMouseMove={e => {
          if (hovered?.type === 'wrapper') {
            setTooltipPos({ x: e.clientX + 12, y: e.clientY + 12 })
          }
        }}
      >
        {!disableEvents &&
          (isIssueNode || isSpecificConditionNode || isInfringementNode) && (
            <IssueNode
              onClick={handleClick}
              onMouseEnter={e => {
                e.stopPropagation()
                setHovered({ id: firstTime, type: 'icon' })
                setTooltipPos({ x: e.clientX + 12, y: e.clientY + 12 })
              }}
              onMouseMove={e => {
                if (hovered?.type === 'icon') {
                  setTooltipPos({ x: e.clientX + 12, y: e.clientY + 12 })
                }
              }}
              onMouseLeave={() => setHovered(null)}
            >
              <IssueNodeCircle background={background}>
                <Icon
                  icon={
                    isIssueNode || isInfringementNode
                      ? 'exclamation-triangle'
                      : firstNode?.icon
                      ? firstNode.icon
                      : 'ship'
                  }
                  type="solid"
                  size="16px"
                  color={
                    isIssueNode || isInfringementNode ? 'red11' : 'blue100'
                  }
                  cursor="pointer"
                />
              </IssueNodeCircle>
            </IssueNode>
          )}
      </Wrapper>
      {!disableTooltip &&
        tooltipPos &&
        !(isIssueNode || isSpecificConditionNode || isInfringementNode) && (
          <IssueNodeTooltip
            visible={hovered?.id === firstTime}
            x={tooltipPos.x}
            y={tooltipPos.y}
          >
            <div>
              <span>
                <Icon icon="truck" type="light" />
                <div>{firstNode?.info?.label}</div>
              </span>
              <span>
                {firstNode?.info?.registrationNumbers?.map(
                  registrationNumber => (
                    <span key={registrationNumber}>{registrationNumber}</span>
                  )
                )}
              </span>
            </div>
            <div>
              {firstNode?.info?.date && (
                <span>
                  {`${firstNode?.info?.date?.getDate()} ${firstNode?.info?.date?.toLocaleString(
                    language,
                    { month: 'short' }
                  )} ${firstNode?.info?.date?.getFullYear()}`}
                </span>
              )}
              <span>
                {firstNode?.info?.time}-{secondNode?.info?.time}
              </span>
            </div>
            <div>
              {duration?.hours && translations.nHours({ n: duration.hours })}{' '}
              {duration?.minutes &&
                translations.nMinutes({ n: duration.minutes })}
            </div>
          </IssueNodeTooltip>
        )}
    </>
  )
}
